/**
 * Eterna Vaults - Client-Side Encryption Service
 * 
 * This service provides AES-256-GCM encryption for all user data
 * before it leaves the user's device, ensuring zero-knowledge architecture.
 */

import CryptoJS from 'crypto-js';
import { scrypt } from 'scrypt-js';

class EncryptionService {
  constructor() {
    this.algorithm = 'AES';
    this.mode = CryptoJS.mode.CBC;
    this.padding = CryptoJS.pad.Pkcs7;
    this.keySize = 256 / 32; // 256 bits
    this.ivSize = 128 / 32; // 128 bits for CBC
    this.tagSize = 0; // No tag for CBC
  }

  /**
   * Generate a cryptographically secure random key
   * @returns {string} Base64 encoded 256-bit key
   */
  generateKey() {
    const key = CryptoJS.lib.WordArray.random(256 / 8);
    return CryptoJS.enc.Base64.stringify(key);
  }

  /**
   * Generate a cryptographically secure random IV
   * @returns {string} Base64 encoded 128-bit IV
   */
  generateIV() {
    const iv = CryptoJS.lib.WordArray.random(128 / 8);
    return CryptoJS.enc.Base64.stringify(iv);
  }

  /**
   * Derive encryption key from user passcode using Scrypt
   * @param {string} passcode - User's passcode
   * @param {string} salt - Base64 encoded salt
   * @returns {Promise<string>} Base64 encoded derived key
   */
  async deriveKey(passcode, salt) {
    try {
      const saltBytes = CryptoJS.enc.Base64.parse(salt);
      const passcodeBytes = CryptoJS.enc.Utf8.parse(passcode);
      
      // Scrypt parameters for strong key derivation
      const N = 16384; // CPU/memory cost parameter
      const r = 8;     // Block size parameter
      const p = 1;     // Parallelization parameter
      const dkLen = 32; // Derived key length (256 bits)

      // Convert to Uint8Array for scrypt-js
      const saltUint8 = new Uint8Array(saltBytes.sigBytes);
      for (let i = 0; i < saltBytes.sigBytes; i++) {
        saltUint8[i] = (saltBytes.words[Math.floor(i / 4)] >>> (24 - (i % 4) * 8)) & 0xff;
      }

      const passcodeUint8 = new Uint8Array(passcodeBytes.sigBytes);
      for (let i = 0; i < passcodeBytes.sigBytes; i++) {
        passcodeUint8[i] = (passcodeBytes.words[Math.floor(i / 4)] >>> (24 - (i % 4) * 8)) & 0xff;
      }

      // Derive key using Scrypt
      const derivedKey = await scrypt(passcodeUint8, saltUint8, N, r, p, dkLen);
      
      // Convert back to Base64
      return CryptoJS.enc.Base64.stringify(CryptoJS.lib.WordArray.create(derivedKey));
    } catch (error) {
      console.error('Key derivation failed:', error);
      throw new Error('Failed to derive encryption key');
    }
  }

  /**
   * Encrypt data using AES-256-GCM
   * @param {string} data - Data to encrypt
   * @param {string} key - Base64 encoded encryption key
   * @param {string} iv - Base64 encoded IV
   * @returns {Object} Encrypted data with IV and tag
   */
  encrypt(data, key, iv) {
    try {
      const keyWordArray = CryptoJS.enc.Base64.parse(key);
      const ivWordArray = CryptoJS.enc.Base64.parse(iv);
      const dataWordArray = CryptoJS.enc.Utf8.parse(data);

      // Encrypt using AES-CBC
      const encrypted = CryptoJS.AES.encrypt(dataWordArray, keyWordArray, {
        iv: ivWordArray,
        mode: this.mode,
        padding: this.padding
      });

      // Extract ciphertext
      const ciphertext = encrypted.ciphertext;

      return {
        ciphertext: CryptoJS.enc.Base64.stringify(ciphertext),
        iv: CryptoJS.enc.Base64.stringify(ivWordArray),
        algorithm: 'AES-256-CBC'
      };
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt data using AES-256-CBC
   * @param {Object} encryptedData - Encrypted data object
   * @param {string} key - Base64 encoded decryption key
   * @returns {string} Decrypted data
   */
  decrypt(encryptedData, key) {
    try {
      const keyWordArray = CryptoJS.enc.Base64.parse(key);
      const ivWordArray = CryptoJS.enc.Base64.parse(encryptedData.iv);
      const ciphertextWordArray = CryptoJS.enc.Base64.parse(encryptedData.ciphertext);

      // Reconstruct the encrypted object
      const encrypted = {
        ciphertext: ciphertextWordArray,
        iv: ivWordArray
      };

      // Decrypt using AES-CBC
      const decrypted = CryptoJS.AES.decrypt(encrypted, keyWordArray, {
        iv: ivWordArray,
        mode: this.mode,
        padding: this.padding
      });

      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedString) {
        throw new Error('Decryption failed - invalid key or corrupted data');
      }

      return decryptedString;
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Encrypt user data with automatic key derivation
   * @param {string} data - Data to encrypt
   * @param {string} passcode - User's passcode
   * @param {string} salt - Base64 encoded salt
   * @returns {Promise<Object>} Encrypted data object
   */
  async encryptUserData(data, passcode, salt) {
    try {
      const key = await this.deriveKey(passcode, salt);
      const iv = this.generateIV();
      const encrypted = this.encrypt(data, key, iv);
      
      return {
        ...encrypted,
        salt: salt,
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('User data encryption failed:', error);
      throw new Error('Failed to encrypt user data');
    }
  }

  /**
   * Decrypt user data with automatic key derivation
   * @param {Object} encryptedData - Encrypted data object
   * @param {string} passcode - User's passcode
   * @returns {Promise<string>} Decrypted data
   */
  async decryptUserData(encryptedData, passcode) {
    try {
      const key = await this.deriveKey(passcode, encryptedData.salt);
      return this.decrypt(encryptedData, key);
    } catch (error) {
      console.error('User data decryption failed:', error);
      throw new Error('Failed to decrypt user data');
    }
  }

  /**
   * Generate a secure salt for key derivation
   * @returns {string} Base64 encoded 256-bit salt
   */
  generateSalt() {
    const salt = CryptoJS.lib.WordArray.random(256 / 8);
    return CryptoJS.enc.Base64.stringify(salt);
  }

  /**
   * Validate encryption parameters
   * @param {Object} encryptedData - Encrypted data to validate
   * @returns {boolean} True if valid
   */
  validateEncryptedData(encryptedData) {
    const requiredFields = ['ciphertext', 'iv', 'tag', 'algorithm', 'salt'];
    return requiredFields.every(field => encryptedData[field]);
  }

  /**
   * Get encryption info for debugging (non-sensitive)
   * @param {Object} encryptedData - Encrypted data object
   * @returns {Object} Encryption metadata
   */
  getEncryptionInfo(encryptedData) {
    return {
      algorithm: encryptedData.algorithm,
      timestamp: encryptedData.timestamp,
      dataSize: encryptedData.ciphertext ? encryptedData.ciphertext.length : 0,
      hasSalt: !!encryptedData.salt,
      hasIV: !!encryptedData.iv,
      hasTag: !!encryptedData.tag
    };
  }
}

// Create singleton instance
const encryptionService = new EncryptionService();

export default encryptionService;
